import { Typography } from '@breetometer/components';
import { alwaysRefetchApiQueryOptions, handleApiError, type IBusiness, showToast } from 'BreetHelpers';
import { useModalApi } from 'BreetHooks';
import {
	selectBusiness,
	setBusiness,
	setIsApprovedForLive,
	setUserData,
	useAppDispatch,
	useAppSelector,
	useGetBusinessQuery,
	useSwitchBusinessMutation,
} from 'BreetRedux';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import AvailableAccount from './AvailableAccount';

const SwitchAccountMenuItems = ({ isMobile }: { isMobile?: boolean }) => {
	const dispatch = useAppDispatch();

	const { closeModalDirect } = useModalApi();

	const navigate = useNavigate();

	const { data: businessesData } = useGetBusinessQuery(undefined, alwaysRefetchApiQueryOptions);

	const [switchBusiness, { isLoading: switchBusinessLoading }] = useSwitchBusinessMutation();

	const activeBusiness = useAppSelector(selectBusiness);

	const [switchingId, setSwitchingId] = useState<string | null>(null);

	const accounts = businessesData?.data?.businesses ?? [];

	const onSwitchSuccess = useCallback(
		(business: IBusiness) => () => {
			dispatch(setBusiness(business));
			dispatch(setIsApprovedForLive(false));
			dispatch(setUserData(undefined));
			showToast('success', `Switched to ${business.name}`);

			if (isMobile) {
				closeModalDirect('switchUserAccountMobile');
			}
		},
		[closeModalDirect, dispatch, isMobile]
	);

	const handleSwitchBusinessAccount = useCallback(
		(business: IBusiness) => () => {
			(document.activeElement as HTMLElement | null)?.blur();

			if (business.id === activeBusiness?.id) return;

			setSwitchingId(business.id);

			switchBusiness({ businessId: business.id })
				.unwrap()
				.then(() => {
					navigate('/home')?.then(onSwitchSuccess(business)).catch(console.error);
				})
				.catch(handleApiError)
				.finally(() => setSwitchingId(null));
		},
		[activeBusiness?.id, navigate, onSwitchSuccess, switchBusiness]
	);

	if (accounts.length <= 1) return null;

	return (
		<div className={`switch-account${isMobile ? '' : '_flyout'}`}>
			<div className='switch-account_panel bg-primary-500 border-dark-navy'>
				<Typography
					as='h3'
					className='switch-account_title color-neutral-50'
				>
					Switch Account
				</Typography>

				<div className='switch-account_list scrollbar'>
					{Array.isArray(accounts) &&
						accounts.map((account, index) => (
							<AvailableAccount
								key={account.id}
								accountName={account.name}
								accountRole={account.role}
								isActive={!!account.isCurrent || account.id === activeBusiness?.id}
								onClick={handleSwitchBusinessAccount(account)}
								lastChild={index === accounts.length - 1}
								isLoading={switchBusinessLoading && switchingId === account.id}
								websiteUrl={account.websiteUrl}
							/>
						))}
				</div>
			</div>
		</div>
	);
};

export default SwitchAccountMenuItems;
